import { AuthenticationError } from '../../../api/authentication';
import { ActionTypeNames, ActionValues, createActionCreators } from '../../../store/actions';
import { ThunkAction } from '../../../store/middleware';
import { UserGroups } from '../constants';

export const sessionActions = createActionCreators<
	{ T: 'SESSION_CREATE_REQUEST' },
	{ T: 'SESSION_CREATE_RECEIVE', P: { accessToken: string, idToken: string, attributes: { [key: string]: string }, groups: UserGroups[] } },
	{ T: 'SESSION_CREATE_FAILURE', P: { error: Error } },
	{ T: 'SESSION_DESTROY', P: { error: Error } },
	{ T: 'SESSION_INVALID', P: { error: AuthenticationError } }
	>('SESSION_CREATE_REQUEST', 'SESSION_CREATE_RECEIVE', 'SESSION_CREATE_FAILURE', 'SESSION_DESTROY', 'SESSION_INVALID');

export const createSession = (username: string, password: string): ThunkAction<any> => async (dispatch, getState, { _deprecatedApi: api }) => {
	dispatch(sessionActions.SESSION_CREATE_REQUEST());
	try {
		const user = await api.authentication.login(username, password);
		const session = user.getSignInUserSession();
		if (!session) throw new AuthenticationError({
			message: 'Current user is not authenticated',
			context: { user: user.getUsername() }
		});
		const [attributes, groups] = await Promise.all([
			api.authentication.getUserAttributes(user),
			api.authentication.getUserGroups(user)
		]);
		const accessToken = session.getAccessToken().getJwtToken();
		const idToken = session.getIdToken().getJwtToken();
		dispatch(sessionActions.SESSION_CREATE_RECEIVE({ accessToken, idToken, attributes, groups }));
	} catch (error) {
		dispatch(sessionActions.SESSION_CREATE_FAILURE({ error }));
		throw error;
	}
};

export const createPasswordlessSession = (username: string, tempSession: string, code: string): ThunkAction<any> =>
	async (dispatch, getState, { _deprecatedApi: api }) => {
		dispatch(sessionActions.SESSION_CREATE_REQUEST());
		try {
			const user = await api.authentication.passwordlessLogin(username, tempSession, code);
			const session = user.getSignInUserSession();
			if (!session) throw new AuthenticationError({
				message: 'Current user is not authenticated',
				context: { user: user.getUsername() }
			});
			const [attributes, groups] = await Promise.all([
				api.authentication.getUserAttributes(user),
				api.authentication.getUserGroups(user)
			]);
			const accessToken = session.getAccessToken().getJwtToken();
			const idToken = session.getIdToken().getJwtToken();
			dispatch(sessionActions.SESSION_CREATE_RECEIVE({ accessToken, idToken, attributes, groups }));
		} catch (error) {
			dispatch(sessionActions.SESSION_CREATE_FAILURE({ error }));
			throw error;
		}
};

export const destroyCurrentSession = (): ThunkAction<any> => async (dispatch, getState, { _deprecatedApi: api }) => {
	const user = await api.authentication.getCurrentUser();
	if (user) {
		await api.authentication.logout(user.getUsername());
		return window.location.reload();
	}
	dispatch(sessionActions.SESSION_DESTROY());
};

type SessionActions = ActionValues<typeof sessionActions>;
type SessionActionTypes = ActionTypeNames<SessionActions>;
export type Actions = SessionActions[keyof SessionActions];
export const ActionTypes = Object.keys(sessionActions).reduce((types, t) => {
	types[t] = t;
	return types;
}, {} as SessionActionTypes);
